import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import AgeValidationErrorModal from "../modal/AgeValidationErrorModal";

// Default passenger structure for TBO API
const createDefaultPassenger = (paxType, contactDetails = {}, fareData = null, isFirst = false, referenceDate = new Date()) => {
  const ref = new Date(referenceDate);
  let dob = "1990-01-01T00:00:00";

  if (paxType === 2) { // Child: 2-18 years, default to 10 years before flight
    const childDob = new Date(ref);
    childDob.setFullYear(ref.getFullYear() - 10);
    dob = childDob.toISOString().split('T')[0] + "T00:00:00";
  } else if (paxType === 3) { // Infant: Under 2 years, default to 1 year before flight
    const infantDob = new Date(ref);
    infantDob.setFullYear(ref.getFullYear() - 1);
    dob = infantDob.toISOString().split('T')[0] + "T00:00:00";
  } else { // Adult: default to 30 years before flight
    const adultDob = new Date(ref);
    adultDob.setFullYear(ref.getFullYear() - 30);
    dob = adultDob.toISOString().split('T')[0] + "T00:00:00";
  }

  return {
    Title: paxType === 1 ? "Mr" : paxType === 2 ? "Mr" : "Master", // Adult, Child, Infant
    FirstName: "",
    LastName: "",
    PaxType: paxType, // 1=Adult, 2=Child, 3=Infant
    DateOfBirth: dob,
    Gender: 1, // 1=Male, 2=Female
    AddressLine1: "India",
    AddressLine2: "",
    City: "Mumbai",
    CountryCode: "IN",
    CountryName: "India",
    Nationality: "IN",
    ContactNo: isFirst ? (contactDetails?.phone || "") : "",
    Email: isFirst ? (contactDetails?.email || "") : "",
    IsLeadPax: isFirst && paxType === 1, // Only first adult is lead passenger
    FFAirlineCode: null,
    FFNumber: null,
    Baggage: [],
    MealDynamic: [],
    SeatDynamic: [],
    SpecialServices: [],
    GSTCompanyAddress: "",
    GSTCompanyContactNumber: "",
    GSTCompanyName: "",
    GSTNumber: "",
    GSTCompanyEmail: "",
    PAN: "", // PAN Card Number
    PassportNo: "", // Passport Number
    PassportExpiry: "", // Passport Expiry Date
    ...(fareData && {
      Fare: {
        BaseFare: fareData.BaseFare || 0,
        Tax: fareData.Tax || 0,
        YQTax: fareData.YQTax || 0,
        AdditionalTxnFeePub: fareData.AdditionalTxnFeePub || 0.0,
        AdditionalTxnFeeOfrd: fareData.AdditionalTxnFeeOfrd || 0.0,
        OtherCharges: fareData.OtherCharges || 0.0,
      }
    })
  };
};

// Helper function to get passenger type label
const getPassengerTypeLabel = (paxType) => {
  switch (paxType) {
    case 1: return "Adult";
    case 2: return "Child";
    case 3: return "Infant";
    default: return "Passenger";
  }
};

// PAN validation function
const validatePAN = (pan) => {
  if (!pan) return { isValid: true, error: "" };

  // PAN format: AAAAA9999A (5 letters, 4 digits, 1 letter)
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

  if (!panRegex.test(pan)) {
    return {
      isValid: false,
      error: "Invalid PAN format. Format: AAAAA9999A (e.g., ABCDE1234F)"
    };
  }

  return { isValid: true, error: "" };
};

// Passport expiry validation function
const validatePassportExpiry = (expiryDate) => {
  if (!expiryDate) return { isValid: true, error: "" };

  const expiry = new Date(expiryDate);
  const today = new Date();

  // Passport should be valid for at least 6 months from today
  const sixMonthsFromNow = new Date();
  sixMonthsFromNow.setMonth(today.getMonth() + 6);

  if (expiry < today) {
    return {
      isValid: false,
      error: "Passport has expired"
    };
  }

  if (expiry < sixMonthsFromNow) {
    return {
      isValid: false,
      error: "Passport should be valid for at least 6 months"
    };
  }

  return { isValid: true, error: "" };
};

function TravellerDetails({
  fareQuoteData,
  trace_id,
  result_idx,
  contactDetails,
  setContactDetails,
  passengers,
  setPassengers,
}) {
  const isInitialized = useRef(false);
  const [ageValidationError, setAgeValidationError] = useState(null);
  const [panErrors, setPanErrors] = useState({});
  const [passportErrors, setPassportErrors] = useState({});
  const [sameAsLead, setSameAsLead] = useState({});
  const [duplicateNameErrors, setDuplicateNameErrors] = useState({});

  // Check for duplicate passenger names
  useEffect(() => {
    if (!passengers || passengers.length === 0) return;

    const errors = {};
    const nameMap = new Map(); // fullName -> [indices]

    passengers.forEach((p, idx) => {
      const fName = p.FirstName?.trim().toLowerCase();
      const lName = p.LastName?.trim().toLowerCase();
      if (fName && lName) {
        const fullName = `${fName} ${lName}`;
        if (nameMap.has(fullName)) {
          nameMap.get(fullName).push(idx);
        } else {
          nameMap.set(fullName, [idx]);
        }
      }
    });

    nameMap.forEach((indices) => {
      if (indices.length > 1) {
        indices.forEach(idx => {
          errors[idx] = "Duplicate name: This name is already entered for another passenger.";
        });
      }
    });
    setDuplicateNameErrors(errors);
  }, [passengers]);

  // Sync passengers who have "Same as Lead" checked when contactDetails change
  useEffect(() => {
    if (!passengers || !passengers.length || !contactDetails) return;

    let hasUpdates = false;
    const updatedPassengers = passengers.map((p, idx) => {
      if (sameAsLead[idx]) {
        const leadEmail = contactDetails.email || "";
        const leadPhone = contactDetails.phone || "";
        if (p.Email !== leadEmail || p.ContactNo !== leadPhone) {
          hasUpdates = true;
          return {
            ...p,
            Email: leadEmail,
            ContactNo: leadPhone,
          };
        }
      }
      return p;
    });

    if (hasUpdates) {
      setPassengers(updatedPassengers);
    }
  }, [contactDetails?.email, contactDetails?.phone, sameAsLead, passengers, setPassengers]);

  // Determine the last segment departure date for age validation
  const lastDepartureDate = useMemo(() => {
    if (!fareQuoteData?.Results?.Segments) return new Date();

    let lastDep = null;
    const segments = fareQuoteData.Results.Segments;

    // Segments is usually Array<Array<Segment>>
    segments.forEach(journey => {
      if (Array.isArray(journey)) {
        journey.forEach(segment => {
          if (segment?.Origin?.DepTime) {
            const depTime = new Date(segment.Origin.DepTime);
            if (!lastDep || depTime > lastDep) {
              lastDep = depTime;
            }
          }
        });
      }
    });

    return lastDep || new Date();
  }, [fareQuoteData]);

  // console.log('fareQuoteData', fareQuoteData)

  // Extract document requirements from fareQuoteData
  const isPanRequired = fareQuoteData?.Results?.IsPanRequiredAtBook || fareQuoteData?.Results?.IsPanRequiredAtTicket || false;
  const isPassportRequired = fareQuoteData?.Results?.IsPassportRequiredAtBook || fareQuoteData?.Results?.IsPassportRequiredAtTicket || false;
  // Parse FareBreakdown to get passenger requirements and fare data
  const passengerRequirements = useMemo(() => {
    if (!fareQuoteData?.Results?.FareBreakdown) {
      return [{ PassengerType: 1, PassengerCount: 1, fareData: null }];
    }

    const breakdown = fareQuoteData.Results.FareBreakdown;
    return breakdown.map(item => ({
      PassengerType: item.PassengerType,
      PassengerCount: item.PassengerCount,
      fareData: {
        Currency: item.Currency,
        BaseFare: item.BaseFare,
        Tax: item.Tax,
        YQTax: item.YQTax,
        AdditionalTxnFeeOfrd: item.AdditionalTxnFeeOfrd,
        AdditionalTxnFeePub: item.AdditionalTxnFeePub,
        PGCharge: item.PGCharge,
      }
    }));
  }, [fareQuoteData]);

  // Initialize passengers based on FareBreakdown - ONLY ONCE
  useEffect(() => {
    if (!isInitialized.current && setPassengers && (!passengers || passengers.length === 0)) {
      const initialPassengers = [];

      passengerRequirements.forEach(({ PassengerType, PassengerCount, fareData }) => {
        for (let i = 0; i < PassengerCount; i++) {
          const isFirstPassenger = initialPassengers.length === 0; // First passenger overall
          const newPassenger = createDefaultPassenger(PassengerType, contactDetails, fareData, isFirstPassenger, lastDepartureDate);
          initialPassengers.push(newPassenger);
        }
      });

      setPassengers(initialPassengers);
      isInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengerRequirements]);

  // Update first adult passenger contact info when contactDetails change
  useEffect(() => {
    if (
      isInitialized.current &&
      setPassengers &&
      passengers &&
      passengers.length > 0 &&
      contactDetails
    ) {
      const firstAdultIndex = passengers.findIndex(p => p.PaxType === 1);
      if (firstAdultIndex === -1) return;

      const firstAdult = passengers[firstAdultIndex];
      const needsUpdate =
        firstAdult.ContactNo !== (contactDetails.phone || "") ||
        firstAdult.Email !== (contactDetails.email || "");

      if (needsUpdate) {
        const updatedPassengers = [...passengers];
        updatedPassengers[firstAdultIndex] = {
          ...firstAdult,
          ContactNo: contactDetails.phone || "",
          Email: contactDetails.email || "",
        };
        setPassengers(updatedPassengers);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactDetails?.email, contactDetails?.phone]);

  // Age validation helper function
  const validateAge = (dob, paxType) => {
    if (!dob) return { isValid: true };

    const birthDate = new Date(dob);
    const today = lastDepartureDate; // Use flight last segment departure date for validation
    const ageInYears = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const dayDiff = today.getDate() - birthDate.getDate();

    // Calculate exact age
    let exactAge = ageInYears;
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      exactAge--;
    }

    // Validation rules
    if (paxType === 2) { // Child: 2-18 years
      if (exactAge < 2 || exactAge > 18) {
        return {
          isValid: false,
          actualAge: `${exactAge} years`,
          requiredAgeRange: "2-18 years"
        };
      }
    } else if (paxType === 3) { // Infant: under 2 years
      if (exactAge >= 2) {
        return {
          isValid: false,
          actualAge: `${exactAge} years`,
          requiredAgeRange: "Under 2 years"
        };
      }
    }

    return { isValid: true };
  };

  // Handler to update a specific passenger field
  const updatePassenger = (index, field, value) => {
    if (!setPassengers || !passengers) return;

    const updatedPassengers = [...passengers];

    // If passenger doesn't exist at this index, create it dynamically
    if (!updatedPassengers[index]) {

      // Helper function to get passenger type and fare data for a specific index
      const getPassengerDataForIndex = (targetIndex) => {
        let currentIndex = 0;
        for (const req of passengerRequirements) {
          for (let i = 0; i < req.PassengerCount; i++) {
            if (currentIndex === targetIndex) {
              return { PassengerType: req.PassengerType, fareData: req.fareData };
            }
            currentIndex++;
          }
        }
        return { PassengerType: 1, fareData: null }; // Default to Adult if not found
      };

      // Fill in any missing passengers up to this index
      while (updatedPassengers.length <= index) {
        const { PassengerType, fareData } = getPassengerDataForIndex(updatedPassengers.length);
        const isFirstPassenger = updatedPassengers.length === 0;
        updatedPassengers.push(createDefaultPassenger(PassengerType, contactDetails, fareData, isFirstPassenger, lastDepartureDate));
      }
    }

    // Handle special field mappings
    if (field === "Title") {
      const gender = (value === "Mr" || value === "Master") ? 1 : 2;
      updatedPassengers[index] = {
        ...updatedPassengers[index],
        Title: value,
        Gender: gender,
      };
    } else {
      let filteredValue = value;
      if (field === "ContactNo") {
        filteredValue = value.replace(/\D/g, "");
      }

      updatedPassengers[index] = {
        ...updatedPassengers[index],
        [field]: filteredValue,
      };

      // Uncheck "Same as Lead" if manually edited
      if (sameAsLead[index] && (field === "Email" || field === "ContactNo")) {
        setSameAsLead(prev => ({ ...prev, [index]: false }));
      }

      // Synchronization: If updating lead passenger's contact info, sync with contactDetails
      if (updatedPassengers[index].IsLeadPax) {
        if (field === "Email") {
          setContactDetails(prev => ({ ...prev, email: filteredValue }));
        } else if (field === "ContactNo") {
          setContactDetails(prev => ({ ...prev, phone: filteredValue }));
        }
      }
    }

    // If updating DOB, validate age for Child and Infant
    if (field === "DateOfBirth") {
      const passenger = updatedPassengers[index];
      const validation = validateAge(value, passenger.PaxType);

      if (!validation.isValid) {
        setAgeValidationError({
          passengerType: passenger.PaxType,
          passengerName: `${passenger.FirstName || ""} ${passenger.LastName || ""}`.trim(),
          actualAge: validation.actualAge,
          requiredAgeRange: validation.requiredAgeRange,
        });
        return; // Don't update if validation fails
      }
    }

    // If updating PAN, validate format
    if (field === "PAN") {
      const validation = validatePAN(value.toUpperCase());
      const newPanErrors = { ...panErrors };

      if (!validation.isValid) {
        newPanErrors[index] = validation.error;
      } else {
        delete newPanErrors[index];
      }

      setPanErrors(newPanErrors);
      // Update with uppercase PAN
      updatedPassengers[index] = {
        ...updatedPassengers[index],
        PAN: value.toUpperCase(),
      };
      setPassengers(updatedPassengers);
      return;
    }

    // If updating Passport Expiry, validate
    if (field === "PassportExpiry") {
      const validation = validatePassportExpiry(value);
      const newPassportErrors = { ...passportErrors };

      if (!validation.isValid) {
        newPassportErrors[`${index}_expiry`] = validation.error;
      } else {
        delete newPassportErrors[`${index}_expiry`];
      }

      setPassportErrors(newPassportErrors);
    }

    setPassengers(updatedPassengers);
  };

  // Handler for toggle "Same as Lead Traveller"
  const handleSameAsLeadChange = (index, isChecked) => {
    setSameAsLead((prev) => ({ ...prev, [index]: isChecked }));

    if (passengers && passengers[index]) {
      const updatedPassengers = [...passengers];
      if (isChecked && contactDetails) {
        updatedPassengers[index] = {
          ...updatedPassengers[index],
          Email: contactDetails.email || "",
          ContactNo: contactDetails.phone || "",
        };
      } else {
        // Clear values when unchecked
        updatedPassengers[index] = {
          ...updatedPassengers[index],
          Email: "",
          ContactNo: "",
        };
      }
      setPassengers(updatedPassengers);
    }
  };

  // Get appropriate title options based on passenger type
  const getTitleOptions = (PassengerType) => {
    if (PassengerType === 1) { // Adult
      return ["Mr", "Ms", "Mrs"];
    } else if (PassengerType === 2) { // Child
      return ["Mr", "Miss"];
    } else { // Infant
      return ["Master", "Miss"];
    }
  };

  // Render passenger input fields
  const renderPassengerFields = () => {
    if (!passengers || passengers.length === 0) {
      // console.log("No passengers to render");
      return null;
    }

    // console.log("Rendering fields for passengers:", passengers);

    let passengerIndex = 0;
    const sections = [];

    passengerRequirements.forEach(({ PassengerType, PassengerCount }) => {
      const typeLabel = getPassengerTypeLabel(PassengerType);

      for (let i = 0; i < PassengerCount; i++) {
        const currentPassenger = passengers[passengerIndex];

        // If passenger doesn't exist, create a default one for display
        // Get fare data for this passenger type
        const fareDataForType = passengerRequirements.find(req => req.PassengerType === PassengerType)?.fareData;
        const isFirstPassenger = passengerIndex === 0;
        const passengerData = currentPassenger || createDefaultPassenger(PassengerType, contactDetails, fareDataForType, isFirstPassenger, lastDepartureDate);

        const displayNumber = i + 1;
        const titleOptions = getTitleOptions(PassengerType);
        const currentIndex = passengerIndex; // Capture current index for closure

        // console.log(`Rendering ${typeLabel} ${displayNumber}, index: ${currentIndex}, data:`, passengerData);

        sections.push(
          <div key={`passenger-${PassengerType}-${i}`} className="mb-10">
            <h3 className="font-semibold text-base mb-4 text-gray-800">
              {typeLabel} {displayNumber}
            </h3>
            <div className="flex flex-col lg:flex-row gap-4">
              <select
                value={passengerData.Title || titleOptions[0]}
                onChange={(e) => updatePassenger(currentIndex, "Title", e.target.value)}
                className="w-full lg:w-[100px] px-3 py-3 border border-[#11111333] rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-700 transition-all h-[50px] cursor-pointer"
              >
                {titleOptions.map(title => (
                  <option key={title} value={title}>{title}</option>
                ))}
              </select>
              <div className="flex-1 flex flex-col">
                <input
                  type="text"
                  placeholder="First Name *"
                  value={passengerData.FirstName || ""}
                  onChange={(e) => updatePassenger(currentIndex, "FirstName", e.target.value)}
                  className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700 transition-all h-[50px] ${duplicateNameErrors[currentIndex] ? 'border-red-500' : 'border-[#11111333]'
                    }`}
                />
              </div>
              <div className="flex-1 flex flex-col">
                <input
                  type="text"
                  placeholder="Last Name *"
                  value={passengerData.LastName || ""}
                  onChange={(e) => updatePassenger(currentIndex, "LastName", e.target.value)}
                  className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700 transition-all h-[50px] ${duplicateNameErrors[currentIndex] ? 'border-red-500' : 'border-[#11111333]'
                    }`}
                />
              </div>
            </div>
            {duplicateNameErrors[currentIndex] && (
              <p className="mt-2 text-sm text-red-600 font-medium bg-red-50 px-3 py-1.5 rounded border border-red-100 flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {duplicateNameErrors[currentIndex]}
              </p>
            )}

            {/* Same as Lead Traveller Checkbox */}
            {!passengerData.IsLeadPax && (
              <div className="flex items-center mt-6">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      id={`same-as-lead-${currentIndex}`}
                      checked={sameAsLead[currentIndex] || false}
                      onChange={(e) => handleSameAsLeadChange(currentIndex, e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 border-2 rounded transition-all duration-200 flex items-center justify-center
                      ${sameAsLead[currentIndex]
                        ? 'bg-orange-500 border-orange-500'
                        : 'bg-white border-gray-300 group-hover:border-orange-400'}`}
                    >
                      {sameAsLead[currentIndex] && (
                        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    Same as Lead Traveller
                  </span>
                </label>
              </div>
            )}

            {/* Email and Mobile Number for each passenger */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <div className="flex flex-col">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email address {passengerData.IsLeadPax ? "*" : (passengerData.PaxType !== 1 ? "(Optional)" : "")}
                </label>
                <input
                  type="email"
                  placeholder="Email address"
                  value={passengerData.Email || ""}
                  onChange={(e) => updatePassenger(currentIndex, "Email", e.target.value)}
                  className="w-full px-4 py-3 border border-[#11111333] rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700 transition-all bg-white h-[50px]"
                />
              </div>
              <div className="flex flex-col">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Mobile Number {passengerData.IsLeadPax ? "*" : (passengerData.PaxType !== 1 ? "(Optional)" : "")}
                </label>
                <div className="flex gap-2">
                  <select
                    className="w-[100px] px-2 py-3 border border-[#11111333] rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-700 transition-all text-sm h-[50px] cursor-pointer"
                    value={contactDetails?.countryCode || "+91"}
                    onChange={(e) => setContactDetails(prev => ({ ...prev, countryCode: e.target.value }))}
                  >
                    <option value="+91">+91 (IN)</option>
                    <option value="+1">+1 (US)</option>
                    <option value="+44">+44 (UK)</option>
                    <option value="+971">+971 (UAE)</option>
                  </select>
                  <input
                    type="tel"
                    placeholder="Number"
                    value={passengerData.ContactNo || ""}
                    onChange={(e) => updatePassenger(currentIndex, "ContactNo", e.target.value)}
                    className="flex-1 px-4 py-3 border border-[#11111333] rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700 transition-all bg-white h-[50px]"
                  />
                </div>
              </div>
            </div>

            {/* Date of Birth Field */}
            < div className="mt-4" >
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth *
                {PassengerType === 2 && (
                  <span className="text-xs text-gray-500 ml-2">(Child: 2-18 years)</span>
                )}
                {PassengerType === 3 && (
                  <span className="text-xs text-gray-500 ml-2">(Infant: Under 2 years)</span>
                )}
              </label>
              <input
                type="date"
                value={passengerData.DateOfBirth ? passengerData.DateOfBirth.split('T')[0] : ""}
                onChange={(e) => {
                  const dateValue = e.target.value;
                  // Convert to ISO format with time
                  const isoDate = dateValue ? `${dateValue}T00:00:00` : "";
                  updatePassenger(currentIndex, "DateOfBirth", isoDate);
                }}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-[#11111333] rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700 transition-all"
              />
            </div >

            {/* PAN Card Field - Only show if required */}
            {
              isPanRequired && PassengerType === 1 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PAN Card Number *
                    <span className="text-xs text-gray-500 ml-2">(Format: AAAAA9999A)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="ABCDE1234F"
                    value={passengerData.PAN || ""}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      // Only allow alphanumeric and max 10 characters
                      if (value.length <= 10) {
                        updatePassenger(currentIndex, "PAN", value);
                      }
                    }}
                    maxLength={10}
                    className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700 transition-all ${panErrors[currentIndex] ? 'border-red-500' : 'border-[#11111333]'
                      }`}
                  />
                  {panErrors[currentIndex] && (
                    <p className="mt-1 text-sm text-red-600">{panErrors[currentIndex]}</p>
                  )}
                </div>
              )
            }

            {/* Passport Fields - Only show if required */}
            {
              isPassportRequired && (
                <>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Passport Number *
                    </label>
                    <input
                      type="text"
                      placeholder="A1234567"
                      value={passengerData.PassportNo || ""}
                      onChange={(e) => updatePassenger(currentIndex, "PassportNo", e.target.value.toUpperCase())}
                      className="w-full px-4 py-3 border border-[#11111333] rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700 transition-all"
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Passport Expiry Date *
                      <span className="text-xs text-gray-500 ml-2">(Must be valid for at least 6 months)</span>
                    </label>
                    <input
                      type="date"
                      value={passengerData.PassportExpiry ? passengerData.PassportExpiry.split('T')[0] : ""}
                      onChange={(e) => {
                        const dateValue = e.target.value;
                        const isoDate = dateValue ? `${dateValue}T00:00:00` : "";
                        updatePassenger(currentIndex, "PassportExpiry", isoDate);
                      }}
                      min={new Date().toISOString().split('T')[0]}
                      className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700 transition-all ${passportErrors[`${currentIndex}_expiry`] ? 'border-red-500' : 'border-[#11111333]'
                        }`}
                    />
                    {passportErrors[`${currentIndex}_expiry`] && (
                      <p className="mt-1 text-sm text-red-600">{passportErrors[`${currentIndex}_expiry`]}</p>
                    )}
                  </div>
                </>
              )
            }

          </div >
        );

        passengerIndex++;
      }
    });

    return sections;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 bg-[#FFF6EC] px-6 py-4">
        <svg
          className="w-5 h-5 text-orange-500 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
        <h2 className="text-base font-semibold text-orange-500">
          Enter Traveller Details
        </h2>
      </div>

      <div className="p-6">
        <div className="mb-6 space-y-4">
          <div className="bg-[#FFFCC7] border border-[#FFFCC7] rounded-lg p-4 flex gap-3">
            <svg width="24" height="24" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
              <path d="M9.16665 18.3333C13.769 18.3333 17.5 14.6023 17.5 9.99996C17.5 5.39759 13.769 1.66663 9.16665 1.66663C4.56427 1.66663 0.833313 5.39759 0.833313 9.99996C0.833313 14.6023 4.56427 18.3333 9.16665 18.3333Z" stroke="#0F172A" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9.16669 6.66663V9.99996" stroke="#0F172A" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 13.3334H8.75" stroke="#0F172A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div className="text-sm">
              <span className="font-bold text-[#0F172A]">Important Note:</span>
              <p className="mt-1 text-gray-700">
                Your booking details will be sent to this email address and mobile number.
              </p>
            </div>
          </div>
          <div className="bg-[#FFFCC7] border border-[#FFFCC7]   rounded-lg p-4 flex gap-3">
            <svg width="24" height="24" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
              <path d="M9.16665 18.3333C13.769 18.3333 17.5 14.6023 17.5 9.99996C17.5 5.39759 13.769 1.66663 9.16665 1.66663C4.56427 1.66663 0.833313 5.39759 0.833313 9.99996C0.833313 14.6023 4.56427 18.3333 9.16665 18.3333Z" stroke="#0F172A" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9.16669 6.66663V9.99996" stroke="#0F172A" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 13.3334H8.75" stroke="#0F172A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div className="text-sm">
              <span className="font-bold text-[#0F172A]">Important Note:</span>
              <p className="mt-1 text-gray-700">
                Please ensure that the names of the passengers on the travel
                documents is the same as on their government issued identity proof.
              </p>
            </div>
          </div>
        </div>

        {/* Dynamic Passenger Fields */}
        <div className="">
          {renderPassengerFields()}
        </div>
      </div>

      {/* Age Validation Error Modal */}
      <AgeValidationErrorModal
        isOpen={!!ageValidationError}
        passengerType={ageValidationError?.passengerType}
        passengerName={ageValidationError?.passengerName}
        actualAge={ageValidationError?.actualAge}
        requiredAgeRange={ageValidationError?.requiredAgeRange}
        onClose={() => setAgeValidationError(null)}
      />
    </div>
  );
}

TravellerDetails.propTypes = {
  fareQuoteData: PropTypes.object,
  trace_id: PropTypes.string,
  result_idx: PropTypes.string,
  contactDetails: PropTypes.object,
  setContactDetails: PropTypes.func,
  passengers: PropTypes.array,
  setPassengers: PropTypes.func,
};

export default TravellerDetails;
